import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../config/api';

const CampusConnect = () => {
  const [form, setForm] = useState({
    instituteName: '',
    campusConnect: '',
    email: '',
    phone: '',
    location: '',
    state: '',
    city: '',
    status: 'active',
  });

  const [updateId, setUpdateId] = useState('');
  const [updateFields, setUpdateFields] = useState({
    instituteName: '',
    campusConnect: '',
    email: '',
    phone: '',
    location: '',
    state: '',
    city: '',
    status: 'active',
  });

  const [deleteId, setDeleteId] = useState('');
  const [loading, setLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [campusConnects, setCampusConnects] = useState([]);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const apiBase = API_BASE_URL;
  const { user } = useAuth();

  // Fetch all campus connects
  const fetchCampusConnects = async () => {
    setFetchLoading(true);
    try {
      const url = searchTerm
        ? `${apiBase}/api/campus-connect?search=${encodeURIComponent(searchTerm)}`
        : `${apiBase}/api/campus-connect`;
      
      const res = await fetch(url);
      const data = await res.json();
      
      if (res.ok && data.success) {
        setCampusConnects(data.data || []);
      } else {
        throw new Error(data?.message || 'Failed to fetch campus connects');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch campus connects');
    } finally {
      setFetchLoading(false);
    }
  };

  useEffect(() => {
    fetchCampusConnects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdateChange = (e) => {
    const { name, value } = e.target;
    setUpdateFields((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (!user?.token) {
        throw new Error('You must be logged in to create a campus connect');
      }

      const res = await fetch(`${apiBase}/api/campus-connect`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`,
        },
        body: JSON.stringify(form),
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data?.message || 'Failed to create campus connect');
      }

      setSuccess('Campus Connect created successfully');
      // Reset form
      setForm({
        instituteName: '',
        campusConnect: '',
        email: '',
        phone: '',
        location: '',
        state: '',
        city: '',
        status: 'active',
      });
      // Refresh list
      fetchCampusConnects();
    } catch (err) {
      setError(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setUpdateLoading(true);

    try {
      if (!user?.token) {
        throw new Error('You must be logged in to update a campus connect');
      }

      if (!updateId) {
        throw new Error('Please enter a Campus Connect ID');
      }

      // Build payload with only provided fields
      const payload = {};
      Object.keys(updateFields).forEach((key) => {
        if (updateFields[key] !== '') {
          payload[key] = updateFields[key];
        }
      });

      if (Object.keys(payload).length === 0) {
        throw new Error('Please provide at least one field to update');
      }

      const res = await fetch(`${apiBase}/api/campus-connect/${updateId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data?.message || 'Failed to update campus connect');
      }

      setSuccess('Campus Connect updated successfully');
      setUpdateId('');
      setUpdateFields({
        instituteName: '',
        campusConnect: '',
        email: '',
        phone: '',
        location: '',
        state: '',
        city: '',
        status: 'active',
      });
      // Refresh list
      fetchCampusConnects();
    } catch (err) {
      setError(err.message || 'Failed to update campus connect');
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleDelete = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setDeleteLoading(true);

    try {
      if (!user?.token) {
        throw new Error('You must be logged in to delete a campus connect');
      }

      if (!deleteId) {
        throw new Error('Please enter a Campus Connect ID');
      }

      const res = await fetch(`${apiBase}/api/campus-connect/${deleteId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${user.token}`,
        },
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data?.message || 'Failed to delete campus connect');
      }

      setSuccess('Campus Connect deleted successfully');
      setDeleteId('');
      // Refresh list
      fetchCampusConnects();
    } catch (err) {
      setError(err.message || 'Failed to delete campus connect');
    } finally {
      setDeleteLoading(false);
    }
  };

  const loadCampusConnectForUpdate = async (id) => {
    if (!id) return;
    
    try {
      const res = await fetch(`${apiBase}/api/campus-connect/${id}`);
      const data = await res.json();
      
      if (res.ok && data.success) {
        setUpdateId(id);
        setUpdateFields({
          instituteName: data.data.instituteName || '',
          campusConnect: data.data.campusConnect || '',
          email: data.data.email || '',
          phone: data.data.phone || '',
          location: data.data.location || '',
          state: data.data.state || '',
          city: data.data.city || '',
          status: data.data.status || 'active',
        });
      }
    } catch (err) {
      setError('Failed to load campus connect data');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Campus Connect Management</h1>
        <p className="text-sm text-gray-600 mt-1">Create, update, or delete campus connect entries.</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
          {success}
        </div>
      )}

      {/* Create Form */}
      <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 space-y-6">
        <h2 className="text-xl font-semibold text-gray-900">Create New Campus Connect</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Institute Name *
            </label>
            <input
              name="instituteName"
              value={form.instituteName}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              placeholder="e.g., MIT"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Campus Connect Name *
            </label>
            <input
              name="campusConnect"
              value={form.campusConnect}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              placeholder="e.g., MIT Campus Connect"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email *
            </label>
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              placeholder="e.g., contact@mit.edu"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone
            </label>
            <input
              type="tel"
              name="phone"
              value={form.phone}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              placeholder="e.g., +1 234-567-8900"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Location
            </label>
            <input
              name="location"
              value={form.location}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              placeholder="e.g., 77 Massachusetts Ave"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              State
            </label>
            <input
              name="state"
              value={form.state}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              placeholder="e.g., Massachusetts"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              City
            </label>
            <input
              name="city"
              value={form.city}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              placeholder="e.g., Cambridge"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status *
            </label>
            <select
              name="status"
              value={form.status}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        <div className="pt-2">
          <button
            type="submit"
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md disabled:opacity-60"
          >
            {loading ? 'Creating...' : 'Create Campus Connect'}
          </button>
        </div>
      </form>

      {/* Update Form */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 space-y-4">
        <h2 className="text-xl font-semibold text-gray-900">Update Campus Connect</h2>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Campus Connect ID *
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={updateId}
              onChange={(e) => setUpdateId(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
              placeholder="Enter Campus Connect ID"
            />
            <button
              type="button"
              onClick={() => loadCampusConnectForUpdate(updateId)}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-md"
            >
              Load
            </button>
          </div>
        </div>
        <form onSubmit={handleUpdate} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Institute Name
              </label>
              <input
                name="instituteName"
                value={updateFields.instituteName}
                onChange={handleUpdateChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Campus Connect Name
              </label>
              <input
                name="campusConnect"
                value={updateFields.campusConnect}
                onChange={handleUpdateChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={updateFields.email}
                onChange={handleUpdateChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone
              </label>
              <input
                type="tel"
                name="phone"
                value={updateFields.phone}
                onChange={handleUpdateChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location
              </label>
              <input
                name="location"
                value={updateFields.location}
                onChange={handleUpdateChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                State
              </label>
              <input
                name="state"
                value={updateFields.state}
                onChange={handleUpdateChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                City
              </label>
              <input
                name="city"
                value={updateFields.city}
                onChange={handleUpdateChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                name="status"
                value={updateFields.status}
                onChange={handleUpdateChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
          <div className="pt-1">
            <button
              type="submit"
              disabled={updateLoading}
              className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-md disabled:opacity-60"
            >
              {updateLoading ? 'Updating...' : 'Update Campus Connect'}
            </button>
          </div>
        </form>
      </div>

      {/* Delete Form */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 space-y-4">
        <h2 className="text-xl font-semibold text-gray-900">Delete Campus Connect</h2>
        <form onSubmit={handleDelete} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Campus Connect ID *
            </label>
            <input
              type="text"
              value={deleteId}
              onChange={(e) => setDeleteId(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              placeholder="Enter Campus Connect ID to delete"
            />
          </div>
          <button
            type="submit"
            disabled={deleteLoading}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md disabled:opacity-60"
          >
            {deleteLoading ? 'Deleting...' : 'Delete Campus Connect'}
          </button>
        </form>
      </div>

      {/* List View */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900">All Campus Connects</h2>
          <div className="flex gap-2">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name or email..."
              className="px-3 py-2 border border-gray-300 rounded-md"
            />
            <button
              onClick={fetchCampusConnects}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-md"
            >
              Refresh
            </button>
          </div>
        </div>

        {fetchLoading ? (
          <div className="text-center py-8 text-gray-500">Loading...</div>
        ) : campusConnects.length === 0 ? (
          <div className="text-center py-8 text-gray-500">No campus connects found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Institute Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Campus Connect
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Phone
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {campusConnects.map((item) => (
                  <tr key={item._id}>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {item.instituteName}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {item.campusConnect}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {item.email}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {item.phone || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {item.city && item.state
                        ? `${item.city}, ${item.state}`
                        : item.location || '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${
                          item.status === 'active'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {item.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      <button
                        onClick={() => {
                          setUpdateId(item._id);
                          loadCampusConnectForUpdate(item._id);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CampusConnect;

